import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, keyframes, styled } from "@mui/material";

/* --- fade in keyframes --- */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* --- containers with theme-aware breakpoints --- */
const ProjectsContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  minHeight: "100vh",
  padding: "40px 20px",
  animation: `${fadeIn} 0.6s ease-in-out`,
  background: "transparent",
}));

const TextSection = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "30%",
  left: "40%",
  maxWidth: 1000,
  zIndex: 10,
  // Desktop
  [theme.breakpoints.down("lg")]: {
    left: "5%",
    top: "12%",
  },
  // Tablet -> stack becomes relative and centered
  [theme.breakpoints.down("md")]: {
    position: "relative",
    top: "auto",
    left: "auto",
    textAlign: "center",
    maxWidth: "100%",
    marginBottom: 40,
    // padding: "0 16px",
  },
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "55%",
  right: "10%",
  transform: "translateY(-50%)",
  zIndex: 10,
  [theme.breakpoints.down("lg")]: {
    right: "5%",
  },
  [theme.breakpoints.down("md")]: {
    position: "relative",
    top: "auto",
    right: "auto",
    transform: "none",
    margin: "0 auto",
    display: "flex",
    justifyContent: "center",
    marginTop: 20,
  },
}));

const StackedGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gridTemplateRows: "repeat(2, 1fr)",
  gap: 45,
  width: "clamp(320px, 40vw, 460px)",
  height: "clamp(320px, 40vw, 460px)",
  placeItems: "center",
  [theme.breakpoints.down("md")]: {
    width: "clamp(300px, 90vw, 420px)",
    height: "clamp(300px, 90vw, 420px)",
    gap: 10,
  },
}));

/* --- FlipInner (new) - inner 3D layer that performs the Y-rotation --- */
const FlipInner = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  transformStyle: "preserve-3d",
  WebkitTransformStyle: "preserve-3d",
  transition: "transform 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
  willChange: "transform",
  WebkitBackfaceVisibility: "hidden",
}));

/* --- NotchedSquare responsive sizes and behavior --- */
const NotchedSquare = styled(Box)(
  ({ theme, isFloating = false, isRotated = false }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    borderRadius: 12,
    background: "#FFFDD0",
    width: "clamp(120px, 18vw, 220px)",
    height: "clamp(120px, 18vw, 220px)",
    transition:
      "box-shadow 0.35s ease, background 0.35s ease, transform 0.35s ease",
    cursor: "pointer",
    border: "2px solid #2d8659",
    transformStyle: "preserve-3d",
    WebkitTransformStyle: "preserve-3d",
    perspective: "1200px",
    overflow: "hidden",
    // outer keeps the angled rotation if rotated
    transform: isRotated ? "rotate(45deg)" : "none",
    boxShadow: isFloating
      ? `-8px -8px 0px rgba(45, 134, 89, 0.07), 0 8px 25px rgba(45, 134, 89, 0.18), inset 0 1px 2px rgba(255,255,255,0.9)`
      : "0 6px 20px rgba(45, 134, 89, 0.12)",

    "&::after": isFloating
      ? {
          content: '""',
          position: "absolute",
          inset: 0,
          margin: 6,
          background:
            "linear-gradient(135deg, rgba(45, 134, 89, 0.08) 0%, transparent 40%)",
          borderRadius: 14,
          zIndex: -1,
          filter: "blur(6px)",
          transition: "all 0.35s ease",
          transform: isRotated ? "rotate(45deg)" : "none",
        }
      : {},

    // on hover: keep outer translations/angle, but flip the inner layer (.flipInner)
    "&:hover": {
      transform: isFloating
        ? isRotated
          ? "translate(6px, 6px) rotate(45deg)"
          : "translate(-6px, -6px)"
        : isRotated
        ? "rotate(45deg)"
        : "none",
      boxShadow: isFloating
        ? `-12px -12px 0px rgba(45, 134, 89, 0.12), 0 12px 35px rgba(45, 134, 89, 0.28), inset 0 2px 4px rgba(255,255,255,0.95)`
        : "0 12px 30px rgba(45, 134, 89, 0.18)",

      // flip the inner layer (this ensures the text on the back is readable and not mirrored)
      "& .flipInner": {
        transform: "rotateY(180deg)",
        WebkitTransform: "rotateY(180deg)",
      },
    },

    [theme.breakpoints.down("md")]: {
      width: "clamp(100px, 30vw, 180px)",
      height: "clamp(100px, 30vw, 180px)",
      borderRadius: 10,
    },
  })
);

/* --- Front / Back content --- */
const FrontContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  textAlign: "center",
  background: "transparent",
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
  position: "absolute",
  top: 0,
  left: 0,
  transform: "rotateY(0deg)",
}));

const BackContent = styled(Box)(({ isRotated = false, theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  textAlign: "center",
  // fontSize: '26px',
  padding: 18,
  background: isRotated
    ? "linear-gradient(135deg, #466ba3ff 0%, #34a853 100%)"
    : "linear-gradient(135deg, #2a7b9b 0%, #3a7e1aff 100%)",
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
  position: "absolute",
  top: 0,
  left: 0,
  transform: "rotateY(180deg)",
  borderRadius: 10,
  color: "white",
  boxShadow: "inset 0 0 16px rgba(255,255,255,0.06)",
}));

const RotatedBackTagline = styled(Typography)({
  fontSize: "clamp(0.9rem, 1.2vw, 1.2rem)",
  fontWeight: 600,
  color: "#ffffff",
  lineHeight: 1.2,
  textAlign: "center",
  textShadow: "0 2px 4px rgba(0,0,0,0.45)",
  fontStyle: "italic",
  transform: "rotate(315deg)", // counter-rotate so text reads upright when box is rotated
});

const NormalBackTagline = styled(Typography)({
  fontSize: "clamp(0.85rem, 1.1vw, 1.4rem)",
  fontWeight: 600,
  color: "#ffffff",
  lineHeight: 1.2,
  textAlign: "center",
  textShadow: "0 2px 4px rgba(0,0,0,0.45)",
  fontStyle: "italic",
});

/* --- Floating container inside grid; grid placement still via sx (project.position) --- */
const FloatingContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  gridColumn: "auto",
  gridRow: "auto",
  zIndex: 2,
  transform: "translate(0, 0)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    transform: "none",
  },
}));

const RotatedContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "85%",
  height: "85%",
  textAlign: "center",
  padding: 12,
  background: "transparent",
  zIndex: 1,
  transform: "rotate(-45deg)",
});

const ShapeContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "85%",
  height: "85%",
  textAlign: "center",
  padding: 12,
  background: "transparent",
  zIndex: 1,
  backfaceVisibility: "hidden",
});

const LogoImage = styled("img")(({ theme }) => ({
  width: "clamp(80px, 15vw, 160px)",
  height: "auto",
  objectFit: "contain",
  filter: "drop-shadow(0 2px 4px rgba(45,134,89,0.2))",
  transition: "all 0.25s ease",
}));

const ProjectTitle = styled(Typography)(({ theme }) => ({
  fontSize: "clamp(0.7rem, 1.2vw, 0.95rem)",
  fontWeight: 700,
  color: "#2d8659",
  lineHeight: 1.15,
  marginBottom: 4,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  textShadow: "0 1px 2px rgba(255,255,255,0.8)",
}));

/* --- Main component --- */
const Projects = () => {
  const navigate = useNavigate();
  const [, setIsVideoPlaying] = useState(false);

  const projects = [
    {
      id: 1,
      title: "Suthra Punjab",
      tagline: "World's largest Waste Management System",
      link: "/SuthraPunjab",
      logo: "/images/CM.png",
      position: {
        gridColumn: "2",
        gridRow: "1",
        isFloating: true,
        isRotated: true,
      },
    },
    {
      id: 2,
      title: "Air Quality",
      tagline: "Decoding the Invisible",
      link: "/AirQualityMenu",
      logo: "/images/EPALogo.png",
      position: { gridColumn: "1", gridRow: "2" },
    },
    {
      id: 3,
      title: "Punjab Clean-Air Program",
      tagline: "Future is Electric",
      link: "/PCAP",
      logo: "/images/pcap.png",
      position: { gridColumn: "2", gridRow: "2" },
    },
    {
      id: 4,
      title: "Wild Life",
      tagline: "Preserving the Balance of Nature",
      link: "/WildLifeMenu",
      logo: "/images/WildlifeLogo.png",
      position: { gridColumn: "1", gridRow: "1" },
    },
  ];

  const handleProjectClick = (project) => {
    navigate(project.link);
  };

  // Handle video end
  const handleVideoEnd = () => {
    setIsVideoPlaying(false);
  };

  return (
    <ProjectsContainer>
      <Box
        sx={{
          // flex: 1,
          // minWidth: "280px",
          width: "54%",
          marginTop: "-1%",
          marginLeft: "22%",
          borderRadius: "10px",
          overflow: "hidden",
          // boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)",
          // background: "#000",
          height: "228px",
          backgroundImage: "url('images/Banner.png')",
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
        }}
      />
      <Box
        sx={{
          // flex: 1,
          // minWidth: "280px",
          width: "630px",
          marginTop: "10%",
          marginLeft: "2%",
          borderRadius: "10px",
          overflow: "hidden",
          boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)",
          background: "#000",
        }}
      >
        <video
          controls={"true"}
          autoPlay
          // muted
          loop
          src="videos/Cop_Main.mp4"
          preload="metadata"
          onEnded={handleVideoEnd}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </Box>
      {/* Top-left text block */}
      <TextSection>
        {/* responsive: row on md+, column on xs */}
        <Stack
          direction={{ xs: "column", md: "column" }}
          spacing={2}
          alignItems="center"
          sx={{ width: "100%", justifyContent: { xs: "center", md: "center" } }}
        >
          {/* Text column */}
          <Stack direction="column" sx={{ flexGrow: 1 }}>
            {/* Line 1 */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "center" },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "2rem", md: "2rem", lg: "2.3rem" },
                  fontWeight: 550,
                  color: "#fff",
                  marginRight: 1,
                  fontFamily: "Dancing Script",
                  fontStyle: "italic",
                  textShadow: "2px 2px 4px rgba(0,0,1,1)",
                }}
              >
                Transforming
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "2rem", md: "3.6rem", lg: "3rem" },
                  fontWeight: 900,
                  color: "#045004ff",
                  lineHeight: 0.9,
                  fontFamily: "Dancing Script",
                  textTransform: "uppercase",
                  letterSpacing: 2,
                  textShadow: "2px 2px 4px rgba(255,255,255,0.7)",
                }}
              >
                PUNJAB
              </Typography>
            </Box>

            {/* Line 2 */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "center" },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "2rem", md: "2.3rem" },
                  fontWeight: 550,
                  color: "#fff",
                  marginRight: 1,
                  fontFamily: "Dancing Script",
                  fontStyle: "italic",
                  textShadow: "2px 2px 4px rgba(0,0,1,1)",
                }}
              >
                for
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.8rem", md: "2rem", lg: "3rem" },
                  fontWeight: { sx: 600, md: 1000 },
                  color: "#0a0f46dc",
                  lineHeight: 0.5,
                  fontStyle: "italic",
                  fontFamily: "Dancing Script",
                  textTransform: "uppercase",
                  letterSpacing: 3,
                  textShadow: "2px 2px 4px rgba(255,255,255,0.7)",
                }}
              >
                CLIMATE
              </Typography>
            </Box>

            {/* Line 3 */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "center" },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.6rem", md: "2.5rem" },
                  fontWeight: { sx: 600, md: 1000 },
                  color: "#0a0f46dc",
                  lineHeight: 0.5,
                  fontFamily: "Dancing Script",
                  textTransform: "uppercase",
                  fontStyle: "italic",
                  letterSpacing: 2,
                  textShadow: "2px 2px 4px rgba(255,255,255,0.7)",
                  marginRight: 1,
                }}
              >
                RESILIENT
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "2rem", md: "2.3rem" },
                  fontWeight: 550,
                  color: "#fff",
                  fontFamily: "Dancing Script",
                  fontStyle: "italic",
                  textShadow: "2px 2px 4px rgba(0,0,1,1)",
                }}
              >
                Future...
              </Typography>
            </Box>
          </Stack>

          {/* Logo */}
          <Box sx={{ textAlign: "center" }}>
            <img
              src="/images/PunjabLogo.png"
              alt="Punjab Logo"
              style={{ width: "clamp(140px, 18vw, 280px)", height: "auto" }}
            />
          </Box>
        </Stack>
      </TextSection>

      {/* Project tiles */}
      <ButtonsContainer>
        <StackedGrid>
          {projects.map((project) => (
            <FloatingContainer
              key={project.id}
              sx={{
                gridColumn: project.position.gridColumn,
                gridRow: project.position.gridRow,
              }}
            >
              <NotchedSquare
                isFloating={project.position.isFloating || false}
                isRotated={project.position.isRotated || false}
                onClick={() => handleProjectClick(project)}
              >
                {/* NEW: inner flipping layer wraps both front and back */}
                <FlipInner className="flipInner">
                  <FrontContent>
                    {project.position.isRotated ? (
                      <RotatedContent>
                        <LogoImage src={project.logo} alt={project.title} />
                        <ProjectTitle>{project.title}</ProjectTitle>
                      </RotatedContent>
                    ) : (
                      <ShapeContent>
                        <LogoImage src={project.logo} alt={project.title} />
                        <ProjectTitle>{project.title}</ProjectTitle>
                      </ShapeContent>
                    )}
                  </FrontContent>

                  <BackContent isRotated={project.position.isRotated || false}>
                    {project.position.isRotated ? (
                      <RotatedBackTagline>{project.tagline}</RotatedBackTagline>
                    ) : (
                      <NormalBackTagline>{project.tagline}</NormalBackTagline>
                    )}
                  </BackContent>
                </FlipInner>
              </NotchedSquare>
            </FloatingContainer>
          ))}
        </StackedGrid>
      </ButtonsContainer>
    </ProjectsContainer>
  );
};

export default Projects;
